import type { Workspace, QbrDeck } from "./types";

/* QBR builder — byte-compatible port of api/src/reporting/qbr.ts so the SPA
 * assembles the same quarterly review offline as the API serves live. */

const QUARTER = (iso: string) => { const d = new Date(iso); return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`; };

export function buildQbr(ws: Workspace, accountId: string, today = "2026-08-17"): QbrDeck | undefined {
  const a = ws.accounts.find((x) => x.id === accountId);
  if (!a) return undefined;
  const owner = ws.users.find((u) => u.id === a.ownerId);
  const trend = a.healthTrend ?? [];
  const healthDelta = trend.length >= 2 ? trend[trend.length - 1] - trend[0] : 0;

  const sigs = ws.signals.filter((s) => s.accountId === accountId);
  const wins = sigs.filter((s) => s.band === "positive").map((s) => s.title);
  const plan = ws.successPlans.find((p) => p.accountId === accountId);
  for (const o of plan?.objectives ?? []) if (o.status === "done") wins.push(`${plan!.title}: ${o.title}`);

  const risks = sigs.filter((s) => s.band !== "positive").map((s) => s.title);

  const nextSteps: string[] = [];
  for (const s of sigs) if (s.status === "open" && s.suggestedPlaybook) nextSteps.push(`Run "${s.suggestedPlaybook}" — ${s.title}`);
  for (const t of ws.tasks.filter((t) => t.accountId === accountId && t.status !== "done")) nextSteps.push(t.title);
  for (const o of plan?.objectives ?? []) if (o.status !== "done") nextSteps.push(`${plan!.title}: ${o.title}`);

  const rn = ws.renewals.find((r) => r.accountId === accountId);

  const npsIds = new Set(ws.surveys.filter((s) => s.type === "nps").map((s) => s.id));
  const npsRs = ws.surveyResponses.filter((r) => npsIds.has(r.surveyId) && r.accountId === accountId);
  const prom = npsRs.filter((r) => r.score >= 9).length;
  const det = npsRs.filter((r) => r.score <= 6).length;
  const nps = npsRs.length ? Math.round(((prom - det) / npsRs.length) * 100) : null;
  const lastComment = npsRs.find((r) => r.comment)?.comment;

  return {
    accountId: a.id, accountName: a.name, ownerName: owner?.name ?? "—", period: QUARTER(today), generatedAt: today,
    headline: { arr: a.arr, health: a.health, healthBand: a.healthBand, healthDelta, renewalDate: a.renewalDate, lifecycleStage: a.lifecycleStage },
    adoption: { seats: a.seats, seatsActive: a.seatsActive, utilization: a.seats ? Math.round((a.seatsActive / a.seats) * 100) : 0 },
    scoreFactors: a.scoreFactors.map((f) => ({ label: f.label, value: f.value, direction: f.direction })),
    nps: { score: nps, responses: npsRs.length, lastComment },
    renewal: rn ? { date: rn.renewalDate, arr: rn.arr, category: rn.category, forecastAmount: rn.forecastAmount, expansionAmount: rn.expansionAmount } : undefined,
    wins, risks, nextSteps: [...new Set(nextSteps)],
  };
}
